import type { ServerResponse } from "http";
import type { ParsedUrlQuery } from "querystring";
import { parseCookies } from "nookies";
import { NextPageContext } from "next";
import { OPTIMIZE_EXPERIMENTS, OPTIMIZE_BUCKETS } from "utils/abTesting/config";
import { ExperimentName, AbTestData } from "./types";

/**
 * Appends `headerNames` to the `Vary` header of the {@link ServerResponse}}
 * (likely from {@link NextPageContext `NextPageContext.res`}), retaining
 * any headers already present.
 *
 * The new `Vary` header value is de-duped and sorted.
 */
export function appendVaryHeaders(
  res: ServerResponse | undefined,
  headerNames: string[]
): void {
  if (!res) return;
  const varyHeader = res?.getHeader("Vary");
  const currentVary = Array.isArray(varyHeader)
    ? varyHeader
    : `${varyHeader || ""}`
        .split(",")
        .map((s: string) => s.trim())
        .filter(Boolean);

  const newVarySet = new Set(currentVary.map((v) => v.toLowerCase()));

  headerNames.forEach((v) => newVarySet.add(v));

  const newVary = Array.from(newVarySet);
  newVary.sort();
  res?.setHeader("Vary", Array.from(newVary).join(" "));
}

/**
 * Determines the active test if it belongs to `experiment`.
 *
 * If the ``?optimize_experiment=`` GET parameter is present in the `query`
 * of `ctx` (formatted as `${experiment}.${variant}`), it returns the
 * {@link AbTestData} for that variant. If `ctx` is a {@link NextPageContext},
 * determines the active test by the presence of an `X-Test-Bucket-${num}`
 * header, otherwise it checks the `atltestbucketv1` cookie in
 * `document.cookies`.
 *
 * @param ctx - {@link NextPageContext} from getInitialProps, or optionally a
 * parsed querystring as `{ query }`
 */
export const determineActiveTest: {
  (experiment: ExperimentName, ctx: NextPageContext): AbTestData | undefined;
  (experiment: ExperimentName, ctx?: { query: ParsedUrlQuery }):
    | AbTestData
    | undefined;
} = (experiment, ctx) => {
  const queryParamRaw = ctx?.query?.optimize_experiment || "";
  const queryParam = Array.isArray(queryParamRaw)
    ? queryParamRaw[0]
    : queryParamRaw;
  const req = ctx && "req" in ctx ? ctx.req : undefined;
  const bucketCookie = parseInt(
    parseCookies(req && { req })["atltestbucketv1"]
  );

  const allBucketNums = Array.from(Object.keys(OPTIMIZE_BUCKETS)).map((b) =>
    parseInt(b)
  ) as (keyof typeof OPTIMIZE_BUCKETS)[];
  const value = allBucketNums.find((bucket) => {
    if (queryParam) {
      return OPTIMIZE_BUCKETS[bucket] === queryParam;
    } else if (req) {
      return req.headers[`x-test-bucket-${bucket}`] === "1";
    } else {
      return bucket === bucketCookie;
    }
  });

  if (value === undefined) return;

  let group: string | undefined = queryParam || OPTIMIZE_BUCKETS[value];

  if (group.indexOf(".") === -1) {
    group = `${experiment}.${group}`;
  } else if (group.indexOf(`${experiment}.`) !== 0) {
    // If the group does not belong to the experiment passed in,
    // don't return the AB test data
    return;
  }
  const { id, groups } = OPTIMIZE_EXPERIMENTS[experiment];
  const [, variant] = group.split(".");
  const variantIndex = [...groups, ""].indexOf(variant);

  return { id, value, group, experiment, variant, variantIndex };
};

/**
 * Given a list of experiment names, return the bucket numbers for all groups
 */
export function getBucketsForExperiments(
  experimentNames: ExperimentName[]
): number[] {
  const experimentBucketNames = new Set<string>();
  const buckets = new Set<number>();

  experimentNames.forEach((experimentName) => {
    OPTIMIZE_EXPERIMENTS[experimentName].groups.forEach((group) => {
      experimentBucketNames.add(`${experimentName}.${group}`);
    });
  });
  Object.entries(OPTIMIZE_BUCKETS).forEach(([bucketNumStr, bucketName]) => {
    if (experimentBucketNames.has(bucketName)) {
      buckets.add(parseInt(bucketNumStr));
      experimentBucketNames.delete(bucketName);
    }
  });
  // Add any shared buckets (i.e. OPTIMIZE_BUCKETS[0] == "control", where
  // ${experimentName}.control is not otherwise listed in OPTIMIZE_BUCKETS)
  experimentNames.forEach((experimentName) => {
    Object.entries(OPTIMIZE_BUCKETS).forEach(([bucketNumStr, groupName]) => {
      if (groupName.indexOf(".") !== -1) return;
      const bucketName = `${experimentName}.${groupName}`;
      if (experimentBucketNames.has(bucketName)) {
        buckets.add(parseInt(bucketNumStr));
      }
    });
  });
  const bucketNums = Array.from(buckets);
  bucketNums.sort();
  return bucketNums;
}
